// apps/pwa-pos/src/lib/org-config.ts
// Sprint 36 — Cache local de la config de la org (moneda_base, monedasOperativas, tasas).
//
// El POS necesita conocer la moneda base y las tasas vigentes incluso offline.
// Datos se refrescan online (PostgREST) y se persisten en localStorage.

import type { SupabaseClient } from '@supabase/supabase-js';
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export interface Tasa {
  monedaOrigen: string;
  monedaDestino: string;
  tasa: number;
  fecha: string;
}

export interface SucursalRef {
  id: string;
  nombre: string;
}

export interface OrgConfig {
  orgId: string;
  /** organizations.nombre. Missing on snapshots written before the header showed it. */
  nombre?: string;
  monedaBase: string;
  monedasOperativas: string[];
  tasas: Tasa[];
  /** SucursalId de la sesión actual (necesario para abrir/cerrar caja). */
  sucursalId?: string | null;
  /** Active branches of this org. Missing on older snapshots. */
  sucursales?: SucursalRef[];
  fetchedAt: string;
}

export function bodegaNombre(
  cfg: OrgConfig | null | undefined,
  sucursalId: string | null,
): string | null {
  if (!cfg || !sucursalId) return null;
  const match = cfg.sucursales?.find((sucursal) => sucursal.id === sucursalId);
  return match?.nombre?.trim() || null;
}

const CACHE_KEY = (orgId: string) => `mibodega.org.config.${orgId}`;

export function readOrgConfig(orgId: string): OrgConfig | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(CACHE_KEY(orgId));
    if (!raw) return null;
    const cfg = JSON.parse(raw) as OrgConfig;
    if (!cfg.monedaBase) return null;
    return cfg;
  } catch {
    return null;
  }
}

export function writeOrgConfig(cfg: OrgConfig): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(CACHE_KEY(cfg.orgId), JSON.stringify(cfg));
  } catch (err) {
    console.warn('[org-config] no se pudo persistir:', err);
  }
}

export function rateIsStale(cfg: OrgConfig, maxAgeHours = 24): boolean {
  const elapsed = Date.now() - new Date(cfg.fetchedAt).getTime();
  return elapsed > maxAgeHours * 60 * 60 * 1000;
}

let supabaseClient: SupabaseClient | null = null;
function getClient(): SupabaseClient | null {
  if (supabaseClient) return supabaseClient;
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
  supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { persistSession: true, autoRefreshToken: true },
  });
  return supabaseClient;
}

interface OrgRow {
  id: string;
  moneda_base: string;
  nombre?: string | null;
}

interface TasaRow {
  moneda_origen_codigo: string;
  moneda_destino_codigo: string;
  tasa: number | string;
  fecha: string;
}

interface SucursalRow {
  id: string;
  nombre?: string | null;
}

/**
 * Refresca la config de la org desde Supabase. Devuelve el snapshot
 * (online), o el cache si offline / si Supabase no responde.
 */
export async function refreshOrgConfig(orgId: string): Promise<OrgConfig | null> {
  const client = getClient();
  const fallback = readOrgConfig(orgId);

  if (!client) return fallback;

  try {
    const { data: org, error: orgErr } = await client
      .from('organizations')
      .select('id, nombre, moneda_base')
      .eq('id', orgId)
      .maybeSingle();

    if (orgErr || !org) {
      console.warn('[org-config] org fetch error:', orgErr?.message);
      return fallback;
    }

    const { data: tasas, error: tasasErr } = await client
      .from('tasas_cambio')
      .select('moneda_origen_codigo, moneda_destino_codigo, tasa, fecha')
      .eq('organization_id', orgId)
      .order('fecha', { ascending: false })
      .limit(200);

    if (tasasErr) {
      console.warn('[org-config] tasas fetch error:', tasasErr.message);
    }

    const { data: sucursales, error: sucErr } = await client
      .from('sucursales_registry')
      .select('id, nombre')
      .eq('organization_id', orgId)
      .eq('activo', true)
      .order('nombre')
      .limit(50);

    if (sucErr) {
      console.warn('[org-config] sucursales fetch error:', sucErr.message);
    }

    const tasasNorm: Tasa[] = ((tasas ?? []) as TasaRow[]).map((t) => ({
      monedaOrigen: t.moneda_origen_codigo,
      monedaDestino: t.moneda_destino_codigo,
      tasa: typeof t.tasa === 'string' ? Number(t.tasa) : t.tasa,
      fecha: t.fecha,
    }));

    const monedasOperativas = Array.from(
      new Set([(org as OrgRow).moneda_base, ...tasasNorm.map((t) => t.monedaOrigen)]),
    ).sort();

    const sucursalRows = (sucursales as SucursalRow[] | null) ?? [];
    const cfg: OrgConfig = {
      orgId,
      nombre: (org as OrgRow).nombre?.trim() || undefined,
      monedaBase: (org as OrgRow).moneda_base,
      monedasOperativas,
      tasas: tasasNorm,
      sucursalId: sucursalRows[0]?.id ?? null,
      sucursales: sucursalRows.flatMap((sucursal) => {
        const nombre = sucursal.nombre?.trim();
        return nombre ? [{ id: sucursal.id, nombre }] : [];
      }),
      fetchedAt: new Date().toISOString(),
    };

    writeOrgConfig(cfg);
    return cfg;
  } catch (err) {
    console.warn('[org-config] refresh failed:', err);
    return fallback;
  }
}

/**
 * Busca la última tasa origen→destino. Devuelve null si no hay.
 */
export function findTasa(cfg: OrgConfig, origen: string, destino: string): number | null {
  if (origen === destino) return 1;
  const match = cfg.tasas.find((t) => t.monedaOrigen === origen && t.monedaDestino === destino);
  if (!match) return null;
  return match.tasa;
}
